/**
 * Vỏ dùng chung cho các màn máy trạm (bảng giờ khởi hành): rail đầu có tiêu đề,
 * đèn kết nối, nút loa + đọc thử, đồng hồ lật; rail chân tuỳ màn; và toàn bộ
 * CSS bảng.
 *
 * Tách ra vì có HAI màn máy trạm dùng chung bộ chrome này — máy trạm đơn hàng
 * (`/station`) và máy trạm sản xuất (`/station/production`). Chép bản thứ hai
 * đồng nghĩa sửa màu/nhịp nháy hai nơi, sớm muộn cũng lệch.
 */

import { useEffect, useState } from 'react'
import { ArrowLeft, Volume2, VolumeX } from 'lucide-react'
import type { ReactNode } from 'react'
import type { SseQueryDataStatus } from '@/hooks/use-sse-query-recovery'

import { m } from '@/lib/paraglide/messages'

interface StationShellProps {
    title: string
    connected: boolean
    dataStatus?: SseQueryDataStatus
    onRetryData?: () => void
    soundEnabled: boolean
    audioBlocked: boolean
    onToggleSound: () => void
    onTestSound: () => void
    onExit: () => void
    /** Nhãn khi còn kết nối. Bỏ trống = "đang nhận đơn" (máy trạm kho); bảng
     *  máy vò không nhận đơn nào nên phải nói khác. */
    liveLabel?: string
    /** Rail chân (tóm tắt hàng đợi). Bỏ trống thì không render rail. */
    footer?: ReactNode
    /** Chỉ máy trạm thật (có station.view) mới có thông báo này; người xem không thấy gì. */
    readinessNotice?: { blockedAt: number; requestId?: string }
    children: ReactNode
}

export function StationShell({
    title,
    connected,
    dataStatus,
    onRetryData,
    soundEnabled,
    audioBlocked,
    onToggleSound,
    onTestSound,
    onExit,
    liveLabel,
    footer,
    readinessNotice,
    children,
}: StationShellProps) {
    // Trạng thái loa gói lại thành một "màu" để rail đầu hiển thị nhất quán.
    const soundTone =
        soundEnabled && !audioBlocked
            ? 'on'
            : soundEnabled && audioBlocked
              ? 'blocked'
              : 'off'
    const soundLabel =
        soundTone === 'blocked'
            ? m.station_audio_blocked()
            : soundTone === 'on'
              ? m.station_sound_on()
              : m.station_enable_sound()

    return (
        <div className="station-board flex h-screen flex-col overflow-hidden">
            <StationBoardStyles />

            {/* Rail đầu — chrome kiểu bảng giờ khởi hành */}
            <header className="station-chrome flex flex-wrap items-center justify-between gap-3 border-b border-[var(--line)] px-5 py-3">
                <div className="flex min-w-0 items-center gap-4">
                    <button
                        type="button"
                        onClick={onExit}
                        aria-label={m.common_aria_back()}
                        className="inline-flex size-9 shrink-0 items-center justify-center rounded-md text-[var(--dim)] transition-colors hover:bg-[var(--panel-2)] hover:text-[var(--ink)]"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="min-w-0">
                        <h1 className="truncate text-lg font-black uppercase leading-none tracking-[0.14em] text-[var(--ink)]">
                            {title}
                        </h1>
                    </div>
                    <ConnectionPill
                        connected={connected}
                        liveLabel={liveLabel}
                    />
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onToggleSound}
                        className={`station-btn ${soundTone} ${
                            soundTone === 'on' ? '' : 'is-attention'
                        }`}
                    >
                        {soundEnabled ? (
                            <Volume2 className="h-4 w-4" />
                        ) : (
                            <VolumeX className="h-4 w-4" />
                        )}
                        <span className="hidden sm:inline">{soundLabel}</span>
                    </button>
                    {soundEnabled && (
                        <button
                            type="button"
                            onClick={onTestSound}
                            className="station-btn ghost"
                        >
                            {m.station_test_sound()}
                        </button>
                    )}
                    <StationClock />
                </div>
            </header>

            {dataStatus && (
                <div className="station-chrome flex flex-wrap items-center justify-between gap-2 border-b border-[var(--line)] px-5 py-2 text-sm">
                    <p
                        role="status"
                        className={
                            dataStatus === 'stale'
                                ? 'text-[var(--alert)]'
                                : 'text-[var(--dim)]'
                        }
                    >
                        {dataStatus === 'stale'
                            ? m.station_data_stale()
                            : dataStatus === 'loading'
                              ? m.station_data_loading()
                              : m.station_data_fresh()}
                    </p>
                    {dataStatus === 'stale' && onRetryData && (
                        <button
                            type="button"
                            className="station-btn"
                            onClick={onRetryData}
                        >
                            {m.station_data_retry()}
                        </button>
                    )}
                </div>
            )}

            {children}

            {readinessNotice && (
                <div
                    className="station-chrome border-t border-[var(--line)] px-5 py-2 text-center text-xs text-[var(--dim)]"
                    role="status"
                >
                    {readinessNotice.requestId
                        ? m.station_readiness_blocked({
                              time: new Intl.DateTimeFormat('vi-VN', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false,
                              }).format(readinessNotice.blockedAt),
                              requestId: readinessNotice.requestId,
                          })
                        : m.station_readiness_blocked_without_request_id({
                              time: new Intl.DateTimeFormat('vi-VN', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false,
                              }).format(readinessNotice.blockedAt),
                          })}
                </div>
            )}

            {footer}
        </div>
    )
}

export function ConnectionPill({
    connected,
    liveLabel,
}: {
    connected: boolean
    liveLabel?: string
}) {
    return (
        <span
            className={`station-pill ${connected ? 'live' : 'down'}`}
            role="status"
        >
            <span className={`led ${connected ? 'led-live' : 'led-off'}`} />
            {connected
                ? (liveLabel ?? m.station_live())
                : m.station_disconnected()}
        </span>
    )
}

export function SummaryStat({
    tone,
    count,
    label,
}: {
    tone: 'amber' | 'ready'
    count: number
    label: string
}) {
    return (
        <span className="flex items-center gap-2">
            <span
                className={`led ${tone === 'amber' ? 'led-amber' : 'led-ready'}`}
            />
            <span className="text-sm font-bold tabular-nums text-[var(--ink)]">
                {count}
            </span>
            <span className="uppercase tracking-[0.12em] text-[var(--dim)]">
                {label}
            </span>
        </span>
    )
}

export function StationClock() {
    const [time, setTime] = useState(new Date())
    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 10_000)
        return () => clearInterval(timer)
    }, [])
    const hh = String(time.getHours()).padStart(2, '0')
    const mm = String(time.getMinutes()).padStart(2, '0')
    return (
        <div className="flap-clock" aria-hidden="true">
            <span className="flap">{hh}</span>
            <span className="flap-sep">:</span>
            <span className="flap">{mm}</span>
        </div>
    )
}

function StationBoardStyles() {
    return (
        <style>{`
.station-board{
    --board:#EEF1F6; --panel:#FFFFFF; --panel-2:#E7EBF2;
    --ink:#1B2430; --dim:#5F6E82; --line:#DBE1EA;
    --amber:#B5730C; --ready:#1E7A46; --live:#1C9A54; --alert:#CF3A46;
    --info:#2563C9;
    color:var(--ink);
    background:
        radial-gradient(130% 90% at 50% -20%, #FFFFFF 0%, rgba(255,255,255,0) 60%),
        var(--board);
    /* App system font (Tailwind's default font-sans) — no monospace, so the
       board matches the rest of the system. */
    font-family: ui-sans-serif, system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
}
.station-chrome{
    font-family: ui-sans-serif, system-ui, sans-serif;
}

/* --- LEDs --- */
.led{ width:.62rem; height:.62rem; border-radius:9999px;
    background:currentColor;
    box-shadow:0 0 0 3px color-mix(in srgb, currentColor 20%, transparent);
    flex:none; }
.led-live{ color:var(--live); animation: stationBlink 1.7s ease-in-out infinite; }
.led-off{ color:var(--alert); }
.led-amber{ color:var(--amber); }
.led-ready{ color:var(--ready); }

/* --- Connection pill --- */
.station-pill{
    display:inline-flex; align-items:center; gap:.5rem;
    padding:.32rem .7rem; border-radius:9999px;
    font-family: system-ui, sans-serif;
    font-size:.72rem; font-weight:800; letter-spacing:.12em; text-transform:uppercase;
    border:1px solid var(--line);
}
.station-pill.live{ color:var(--live);
    border-color:color-mix(in srgb, var(--live) 34%, var(--line));
    background:color-mix(in srgb, var(--live) 12%, var(--panel)); }
.station-pill.down{ color:var(--alert);
    border-color:color-mix(in srgb, var(--alert) 40%, var(--line));
    background:color-mix(in srgb, var(--alert) 12%, var(--panel));
    animation: stationBlink 1.1s ease-in-out infinite; }

/* --- Chrome buttons --- */
.station-btn{
    display:inline-flex; align-items:center; gap:.5rem;
    padding:.45rem .8rem; border-radius:8px;
    font-family: system-ui, sans-serif; font-size:.82rem; font-weight:700;
    letter-spacing:.02em; border:1px solid var(--line); color:var(--dim);
    background:var(--panel); transition:background .15s, color .15s, border-color .15s;
    box-shadow:0 1px 1px rgba(20,30,50,.04);
}
.station-btn:hover{ color:var(--ink);
    border-color:color-mix(in srgb, var(--ink) 22%, var(--line)); }
.station-btn.on{ color:var(--live); border-color:color-mix(in srgb,var(--live) 40%,var(--line));
    background:color-mix(in srgb,var(--live) 10%,var(--panel)); }
.station-btn.blocked{ color:var(--amber); border-color:color-mix(in srgb,var(--amber) 45%,var(--line));
    background:color-mix(in srgb,var(--amber) 12%,var(--panel)); }
.station-btn.off{ color:var(--info); border-color:color-mix(in srgb,var(--info) 40%,var(--line));
    background:color-mix(in srgb,var(--info) 10%,var(--panel)); }
.station-btn.is-attention{ animation: stationPulse 1.8s ease-in-out infinite; }
.station-btn.ghost{ color:var(--dim); background:transparent; box-shadow:none; }

/* --- Split-flap clock --- */
.flap-clock{ display:flex; align-items:center; gap:.28rem; }
.flap{
    position:relative; min-width:2.4ch; padding:.28rem .5rem;
    font-variant-numeric: tabular-nums;
    font-size:1.5rem; font-weight:800; line-height:1; text-align:center;
    color:var(--ink); border-radius:7px; border:1px solid var(--line);
    background:linear-gradient(#FFFFFF 0%, #EEF1F6 50%, #E5EAF1 50%, #FBFCFE 100%);
    box-shadow: inset 0 1px 0 #FFFFFF, 0 1px 2px rgba(20,30,50,.14);
}
.flap::after{ content:""; position:absolute; left:5px; right:5px; top:50%;
    height:1px; transform:translateY(-.5px); background:rgba(20,30,50,.16); }
.flap-sep{ font-size:1.4rem; font-weight:800; color:var(--dim); }

/* --- Board grid --- */
.board-grid{
    display:grid;
    grid-template-columns:88px 250px minmax(160px,1fr) minmax(150px,1.25fr) 172px 150px;
    align-items:center; gap:1rem; padding:0 1.25rem;
}
/* Grid items default to min-width:auto, so long content (order codes ~18 chars)
   overflows into the next column. Force min-width:0 so each cell stays in lane. */
.board-grid > *{ min-width:0; }
.legend{
    height:38px; font-size:.68rem; font-weight:800; letter-spacing:.16em;
    text-transform:uppercase; color:var(--dim);
    border-bottom:1px solid var(--line);
    background:var(--panel-2);
    position:sticky; top:0; z-index:2;
}

/* --- Rows --- */
.board-list{ perspective:1500px; background:var(--panel); }
.board-row{
    position:relative; min-height:66px;
    border-bottom:1px solid var(--line);
    transform-style:preserve-3d; transform-origin:top center;
    cursor:pointer; transition:background .18s;
}
.board-row::before{
    content:""; position:absolute; left:0; top:0; bottom:0; width:4px;
    background:var(--gate);
    box-shadow:0 0 8px color-mix(in srgb, var(--gate) 55%, transparent);
    opacity:.65; transition:opacity .18s;
}
.board-row.confirmed{ --gate:var(--amber); }
.board-row.stockout{ --gate:var(--ready); }
.board-row:hover{ background:color-mix(in srgb, var(--ink) 4%, var(--panel)); }
.board-row:hover::before{ opacity:1; }
.board-row:focus-visible{ outline:2px solid var(--amber); outline-offset:-2px; }

.board-row.arrive{
    background:color-mix(in srgb, var(--amber) 12%, var(--panel));
    animation: stationFlap 720ms cubic-bezier(.2,.9,.2,1) both;
}
.board-row.arrive::before{ opacity:1; }
.board-row.arrive::after{
    content:""; position:absolute; inset:0; pointer-events:none;
    background:linear-gradient(100deg, transparent 22%,
        color-mix(in srgb, var(--amber) 24%, transparent) 50%, transparent 78%);
    transform:translateX(-100%);
    animation: stationSweep 900ms ease 140ms 1;
}

/* --- Status gate tags --- */
.gate-tag{
    display:inline-flex; align-items:center;
    padding:.28rem .7rem; border-radius:7px;
    font-family: system-ui, sans-serif; font-weight:900;
    font-size:.72rem; letter-spacing:.08em; text-transform:uppercase;
    border:1px solid transparent; white-space:nowrap;
}
.gate-amber{ color:var(--amber);
    background:color-mix(in srgb, var(--amber) 14%, var(--panel));
    border-color:color-mix(in srgb, var(--amber) 40%, transparent); }
.gate-ready{ color:var(--ready);
    background:color-mix(in srgb, var(--ready) 14%, var(--panel));
    border-color:color-mix(in srgb, var(--ready) 40%, transparent); }

/* --- Machine board (production station) --- */
.board-grid.machine-row, .board-grid.legend.machine-legend{
    grid-template-columns:120px 190px minmax(220px,1fr) 190px;
}
.machine-row{
    position:relative; min-height:74px;
    border-bottom:1px solid var(--line);
    align-items:center;
    transform-style:preserve-3d; transform-origin:top center;
}
.machine-row::before{
    content:""; position:absolute; left:0; top:0; bottom:0; width:4px;
    background:var(--gate);
    box-shadow:0 0 8px color-mix(in srgb, var(--gate) 55%, transparent);
    opacity:.65;
}
.machine-row.running{ --gate:var(--ready); }
.machine-row.stopped{ --gate:var(--dim); }
.machine-row.offline{ --gate:var(--amber); }
.machine-row.error{ --gate:var(--alert); }
.machine-row.arrive{
    background:color-mix(in srgb, var(--amber) 14%, var(--panel));
    animation: stationFlap 720ms cubic-bezier(.2,.9,.2,1) both;
}
.machine-row.arrive::before{ opacity:1; }

/* Progress bar — creeps on the 1s tick so it never snaps back on poll. */
.machine-track{
    flex:1 1 auto; min-width:0; height:.7rem; border-radius:9999px;
    background:var(--panel-2); overflow:hidden;
    border:1px solid var(--line);
}
.machine-fill{
    display:block; height:100%; border-radius:9999px;
    background:var(--gate, var(--ready));
    transition:width 1s linear;
}
.machine-tag-running{ color:var(--ready);
    background:color-mix(in srgb, var(--ready) 14%, var(--panel));
    border-color:color-mix(in srgb, var(--ready) 40%, transparent); }
.machine-tag-stopped{ color:var(--dim);
    background:var(--panel-2); border-color:var(--line); }
.machine-tag-offline{ color:var(--amber);
    background:color-mix(in srgb, var(--amber) 14%, var(--panel));
    border-color:color-mix(in srgb, var(--amber) 40%, transparent); }
.machine-tag-error{ color:var(--alert);
    background:color-mix(in srgb, var(--alert) 14%, var(--panel));
    border-color:color-mix(in srgb, var(--alert) 40%, transparent); }

.empty-blink{ animation: stationBlink 2.6s ease-in-out infinite; }

@keyframes stationFlap{
    0%{ transform:rotateX(-90deg); opacity:0; }
    55%{ transform:rotateX(8deg); opacity:1; }
    100%{ transform:rotateX(0); }
}
@keyframes stationSweep{ to{ transform:translateX(100%); } }
@keyframes stationBlink{ 0%,100%{ opacity:1; } 50%{ opacity:.4; } }
@keyframes stationPulse{ 0%,100%{ opacity:1; } 50%{ opacity:.72; } }

@media (prefers-reduced-motion: reduce){
    .board-row.arrive, .machine-row.arrive{ animation:none; }
    .board-row.arrive::after{ display:none; }
    .led-live, .station-pill.down, .station-btn.is-attention, .empty-blink{
        animation:none; }
}

/* --- Fleet locate/camera column --- */
.station-fleet-aside{ border-left:1px solid var(--line); background:var(--panel); }
.station-fleet{ display:flex; flex-direction:column; width:100%; min-height:0; }
.station-fleet-head{
    display:flex; align-items:center; gap:.6rem; padding:.7rem 1rem;
    border-bottom:1px solid var(--line); background:var(--panel-2);
}
.station-fleet-title{
    font-weight:800; text-transform:uppercase; letter-spacing:.12em;
    font-size:.72rem; color:var(--ink);
}
.station-fleet-count{
    display:inline-flex; align-items:center; gap:.4rem; margin-left:auto;
    font-size:.72rem; font-weight:700; color:var(--dim);
}
.station-fleet-sync{
    width:.55rem; height:.55rem; border-radius:9999px; background:var(--live);
    animation: stationBlink 1s ease-in-out infinite;
}
.station-fleet-scroll{
    flex:1; min-height:0; overflow-y:auto; padding:.75rem;
    display:flex; flex-direction:column; gap:.75rem;
}
.station-fleet-empty{
    flex:1; display:flex; align-items:center; justify-content:center;
    padding:2rem 1rem; text-align:center; color:var(--dim);
    letter-spacing:.12em; text-transform:uppercase; font-size:.8rem;
}
.station-fleet-foot{
    border-top:1px solid var(--line); padding:.5rem 1rem; font-size:.7rem;
    color:var(--dim); text-transform:uppercase; letter-spacing:.08em;
}

/* Spotlight */
.station-spot{
    display:flex; flex-direction:column; gap:.6rem;
    border:1px solid var(--line); border-radius:12px; overflow:hidden;
    background:var(--panel); box-shadow:0 1px 3px rgba(20,30,50,.06);
}
.station-spot-head{ display:flex; align-items:center; gap:.6rem; padding:.6rem .75rem .1rem; }
.station-spot-plate{ font-size:1.15rem; font-weight:900; letter-spacing:.02em; color:var(--ink); }
.station-spot-speed{ font-variant-numeric:tabular-nums; font-weight:800; color:var(--amber); }
.station-spot-pin{
    margin-left:auto; display:inline-flex; padding:.35rem; border-radius:8px;
    border:1px solid var(--line); color:var(--dim); background:var(--panel);
}
.station-spot-pin.is-pinned{
    color:var(--amber); border-color:color-mix(in srgb,var(--amber) 45%,var(--line));
    background:color-mix(in srgb,var(--amber) 12%,var(--panel));
}
.station-spot-nocam{
    aspect-ratio:16/9; display:flex; align-items:center; justify-content:center;
    background:#111; color:rgba(255,255,255,.6); font-size:.75rem;
}
/* Adaptive camera grid: 1 channel -> full; 2+ -> 2 columns (3-4 wrap to 2x2).
   The thin gap over the --line background draws hairlines between cells. */
.station-spot-cams{
    display:grid; grid-template-columns:repeat(2,1fr); gap:2px;
    background:var(--line);
}
.station-spot-cams[data-cams="1"]{ grid-template-columns:1fr; }
.station-spot-addr{ padding:0 .75rem; font-size:.82rem; color:var(--ink); }
.station-spot-stats{
    display:grid; grid-template-columns:1fr 1fr; gap:.4rem .75rem;
    padding:.2rem .75rem .75rem; margin:0;
}
.station-spot-stat dt{ font-size:.62rem; text-transform:uppercase; letter-spacing:.08em; color:var(--dim); }
.station-spot-stat dd{ margin:0; font-size:.82rem; font-weight:600; font-variant-numeric:tabular-nums; color:var(--ink); }

/* Rows */
.station-fleet-list{ display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.4rem; }
.station-fleet-row{
    display:flex; gap:.6rem; align-items:center; text-align:left;
    padding:.4rem; border:1px solid var(--line); border-radius:10px;
    background:var(--panel); transition:background .15s, border-color .15s;
}
.station-fleet-row:hover{ border-color:color-mix(in srgb,var(--ink) 20%,var(--line)); }
.station-fleet-row.is-active{ border-color:color-mix(in srgb,var(--amber) 45%,var(--line)); }
.station-fleet-row.is-departed{ animation: stationSweepRow 1.2s ease 1; }
.station-fleet-thumb{
    width:64px; height:40px; border-radius:6px; overflow:hidden; background:#111;
    flex:none; display:flex; align-items:center; justify-content:center;
}
.station-fleet-thumb img{ width:100%; height:100%; object-fit:cover; }
.station-fleet-thumb-empty{ font-size:.55rem; color:rgba(255,255,255,.5); text-align:center; padding:0 .2rem; }
.station-fleet-body{ min-width:0; flex:1; display:flex; flex-direction:column; gap:.1rem; }
.station-fleet-top{ display:flex; align-items:baseline; gap:.5rem; }
.station-fleet-plate{ font-weight:800; color:var(--ink); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.station-fleet-speed{ margin-left:auto; font-variant-numeric:tabular-nums; font-weight:700; color:var(--amber); flex:none; }
.station-fleet-addr{ font-size:.72rem; color:var(--dim); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
@keyframes stationSweepRow{
    0%{ background:color-mix(in srgb,var(--amber) 22%,var(--panel)); }
    100%{ background:var(--panel); }
}
@media (prefers-reduced-motion: reduce){
    .station-fleet-row.is-departed, .station-fleet-sync{ animation:none; }
}
        `}</style>
    )
}
